import React, { FC, Fragment, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { InvoiceLineItem, InvoiceType } from 'models/invoice';
import { TableContainer, Table, TableHead, TableBody, TableRow, TableCell } from '@mui/material';
import { useMutation } from '@apollo/client/react';
import { certifyUnCertifyOrderTemplate } from 'apollo/templates/order';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import InvoiceLineItemComponent from './invoice-line-item.component';
import ConfirmDialog from 'components/shared/confirm-dialog/confirm-dialog.component';
import Loader from 'components/shared/loader/loader.component';
import { Order } from 'models/order';
import { CONSTANTS } from 'config/constants';
import CertifyDialog from 'components/shared/certify-dialog/certify-dialog.component';
import { currentOrgVar } from 'apollo/reactive-variables/organization';
import { customerSettings } from 'utils/customerSettings';

type InvoiceLineItemListProps = {
    lineItems: InvoiceLineItem[];
    invoiceType: InvoiceType;
    isDentist: boolean;
    currency: string;
    handleCertifyUnCertify: (order: Order) => void;
};
const InvoiceLineItemList: FC<InvoiceLineItemListProps> = ({
    lineItems,
    isDentist,
    invoiceType,
    currency,
    handleCertifyUnCertify,
}: InvoiceLineItemListProps) => {
    const { t: translate } = useTranslation(['orders', 'common']);
    const [isOpen, setIsOpen] = useState(false);
    const [isPriceSpecOpen, setIsPriceSpecOpen] = useState(false);
    const [lineItemIndex, setLineItemIndex] = useState(-1);
    const [invoiceLineItems, setInvoiceLineItems] = useState<InvoiceLineItem[]>([]);
    const currentOrganization = currentOrgVar();
    const { showBoundary } = useDenthubErrorBoundary();
    const isCertificationEnabled = customerSettings.getCustomerSetting(
        currentOrganization.customerSettings,
        CONSTANTS.SETTINGS_NAMES.CERTIFICATION,
    );

    useEffect(() => {
        setInvoiceLineItems(lineItems);
    }, [lineItems]);

    const [certifyUnCertifyOrder, { loading }] = useMutation(certifyUnCertifyOrderTemplate, {
        onCompleted: ({ certifyUnCertifyOrder: order }) => {
            handleCertifyUnCertify(order);
            // TODO: check/find a smart way to update invoice list cache based on response and filters selected
            // For now remove certificationStatus filter from localStorage
            const filters = JSON.parse(localStorage.getItem('invoiceListFilter') || '{}');
            delete filters.certificationStatus;
            localStorage.setItem('invoiceListFilter', JSON.stringify(filters));
        },
        onError(error) {
            showBoundary(error);
        },
    });

    const handleClose = () => {
        setIsOpen(false);
        setIsPriceSpecOpen(false);
    };

    const onConfirm = async () => {
        await certifyUnCertifyOrder({
            variables: { orderId: (invoiceLineItems[lineItemIndex]?.order as Order)?._id as string },
        });
        setIsOpen(false);
        setIsPriceSpecOpen(false);
    };

    if (loading) {
        return <Loader />;
    }

    return (
        <Fragment>
            <TableContainer>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            {isCertificationEnabled && isDentist && invoiceType !== InvoiceType.SINGLE ? (
                                <TableCell>{translate('orders:CERTIFICATION')}</TableCell>
                            ) : undefined}
                            <TableCell>{translate('common:DESCRIPTION')}</TableCell>
                            <TableCell align="right">{translate('orders:QUANTITY')}</TableCell>
                            <TableCell align="right">{translate('orders:UNIT_PRICE')}</TableCell>
                            <TableCell align="right">{translate('orders:TOTAL_EXCL_VAT')}</TableCell>
                            <TableCell align="right">{translate('orders:VAT')}</TableCell>
                            <TableCell align="right">{translate('orders:TOTAL_INCL_VAT')}</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {invoiceLineItems.map((lineItem: InvoiceLineItem, index: number) => (
                            <InvoiceLineItemComponent
                                key={index}
                                currency={currency}
                                invoiceType={invoiceType}
                                openPriceSpecDialog={() => {
                                    setLineItemIndex(index);
                                    setIsPriceSpecOpen(true);
                                }}
                                openConfirmDialog={() => {
                                    setLineItemIndex(index);
                                    setIsOpen(true);
                                }}
                                invoiceLineItem={lineItem}
                                isDentist={isDentist}
                                isCertificationEnabled={isCertificationEnabled}
                            />
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
            <ConfirmDialog
                open={isOpen}
                handleConfirm={onConfirm}
                handleClose={handleClose}
                dialogTitle={translate('orders:REMOVE_CERTIFICATION')}
                dialogContent={translate('orders:REMOVE_CERTIFICATION_INFO')}
                confirmBtnLabel={translate('orders:REMOVE_CERTIFICATION')}
            />
            <CertifyDialog
                open={isPriceSpecOpen}
                handleConfirm={onConfirm}
                currency={currency}
                handleClose={handleClose}
                showUserAndDate
                restorations={(invoiceLineItems[lineItemIndex]?.order as Order)?.restorations || []}
            />
        </Fragment>
    );
};

export default InvoiceLineItemList;
